//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import React, { ReactElement } from 'react';
import {
  ControllerFieldState,
  FieldPath,
  FieldValues,
  RegisterOptions,
  Controller,
  ControllerRenderProps,
  Control,
  ControllerProps,
} from 'react-hook-form';

/**
 * Represents the props for the FormFieldController component, extending the necessary properties
 * for controller-rendered fields and their validation state.
 *
 * @template T - A generic type extending FieldValues to ensure type safety for the form values.
 * @template TFieldName - A generic type parameter extends FieldPath<T>, representing the path to a specific form value.
 *
 * @extends {Pick<ControllerFieldState, 'error' | 'invalid'>} - Picks the 'error' and 'invalid'
 * properties from ControllerFieldState to indicate the validation state of the field.
 * @extends {ControllerRenderProps<T, TFieldName>} - Extends from ControllerRenderProps to include
 * all necessary properties required for rendering and managing the field.
 */
export interface FormFieldControllerProps<
  T extends FieldValues,
  TFieldName extends FieldPath<T>,
> extends Pick<ControllerFieldState, 'error' | 'invalid'>,
    ControllerRenderProps<T, TFieldName> {}

/**
 * A React component that leverages the React Hook Form Controller to manage form fields.
 * This component abstracts the React Hook Form Controller component, making it easier to
 * use and establishing a more user-friendly name.
 *
 * @template T - A generic type parameter extending FieldValues, representing the shape of the form values.
 * @template TFieldName - A generic type parameter extends FieldPath<T>, representing the path to a specific form value.
 *
 * @param {Object} props - The component props.
 * @param {Control<T>} props.control - The `Control` object from React Hook Form, used to register the input.
 * This is returned by both useForm() and useFormContext()
 * @param {(props: FormFieldController<T, TFieldName>) => ReactElement} props.children - A render prop function that
 * returns the actual input element. This function receives `FormFieldControllerProps`, including errors
 * and methods to bind the input.
 * @param {FieldPath<T>} props.name - The name of the field within the form values. This name should match
 * the property name in the form values type `T`.
 * @param {RegisterOptions<T, TFieldName>?} props.rules - Optional. The validation rules for the field,
 * such as `required`, `maxLength`, etc. These are defined using the RegisterOptions from React Hook Form.
 *
 * @returns {ReactElement} - A React Element that renders the form field within a Controller, passing down
 * the necessary props and validation state to the child input element.
 */
export const FormFieldController = <
  T extends FieldValues,
  TFieldName extends FieldPath<T>,
>({
  control,
  children: Field,
  name,
  rules, // validation rules eg: { required: true, maxLength: 20 }
  ...props
}: {
  control: Control<T>;
  children: (props: FormFieldControllerProps<T, TFieldName>) => ReactElement;
  name: TFieldName;
  rules?: RegisterOptions<T, TFieldName>;
} & Partial<
  Omit<ControllerProps<T, TFieldName>, 'name' | 'rules' | 'control'>
>): ReactElement => (
  <Controller
    {...props}
    control={control}
    name={name}
    rules={rules}
    render={
      ({ field, fieldState: { error, invalid } }) =>
        Field({ ...field, error, invalid }) // pass down FormFieldControllerProps
    }
  />
);
